import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import { news } from "@/mocks/news";
import { formatFullDateCN, formatTime } from "@/utils/format";

export default function NewsDetailPage() {
  const { newsId } = useParams();
  const item = news.find((n) => n.id === newsId);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  if (!item) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-article-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">该资讯不存在或已删除</h1>
        <p className="mt-2 text-foreground-600">您可以返回资讯列表，浏览其他篮球报道与分析。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = news.filter((n) => n.id !== item.id && n.category === item.category).slice(0, 3);
  const fallbackRelated = news.filter((n) => n.id !== item.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制，分享给同样热爱篮球的朋友吧");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5 flex-wrap">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/news" className="hover:text-primary-700 cursor-pointer">
          篮球资讯
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800 truncate max-w-[200px]">{item.category}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <article className="min-w-0">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label font-semibold">
            {item.category}
          </span>
          <h1 className="mt-4 font-heading font-semibold text-2xl md:text-4xl text-foreground-950 leading-tight">
            {item.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-newspaper-line" />
              {item.source}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {formatFullDateCN(item.publishTime)} {formatTime(item.publishTime)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-book-open-line" />
              约 {item.readTime} 分钟阅读
            </span>
          </div>

          <div className="mt-6 rounded-lg overflow-hidden border border-background-200">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 篮球资讯`}
              className="w-full h-64 md:h-96 object-top object-cover"
            />
          </div>

          <div className="mt-8 space-y-5">
            <p className="text-base md:text-lg text-foreground-800 leading-relaxed font-medium">
              {item.summary}
            </p>
            {item.content.map((p, i) => (
              <p key={i} className="text-base text-foreground-700 leading-loose">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500 mr-1">相关标签：</span>
            {item.tags.map((t) => (
              <Link
                key={t}
                to="/news"
                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-background-200 transition-colors cursor-pointer"
              >
                #{t}
              </Link>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap items-center gap-3">
            <span className="text-sm text-foreground-600">分享这篇资讯：</span>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-links-line" />
              复制链接
            </button>
            <button
              type="button"
              onClick={() => setToast("已生成分享卡片（演示效果）")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-background-300 text-foreground-800 hover:bg-background-100 text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-line" />
              分享到社交平台
            </button>
          </div>
        </article>

        <aside className="space-y-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-links-line text-accent-600" />
              相关推荐
            </h2>
            <div className="space-y-4">
              {relatedList.map((r) => (
                <Link key={r.id} to={`/news/${r.id}`} className="group flex gap-3 cursor-pointer">
                  <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-100">
                    <img
                      src={r.cover}
                      alt={r.title}
                      title={`${r.title} - 篮球资讯`}
                      className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {r.title}
                    </h3>
                    <span className="mt-1 block text-[11px] text-foreground-500">{r.category}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-primary-950 p-5">
            <h2 className="font-heading text-base text-background-50 mb-2">想看比赛直播？</h2>
            <p className="text-sm text-primary-200 leading-relaxed">
              前往赛程页，查看今日与未来一周的全部篮球赛事安排。
            </p>
            <Link
              to="/schedule"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line" />
              查看赛程
            </Link>
          </div>
        </aside>
      </div>

      <section className="mt-12">
        <div className="flex items-end justify-between gap-4 mb-5">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
            更多资讯推荐
          </h2>
          <Link to="/news" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap">
            全部资讯
            <i className="ri-arrow-right-s-line" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {news
            .filter((n) => n.id !== item.id)
            .slice(0, 3)
            .map((n) => (
              <NewsCard key={n.id} item={n} variant="grid" />
            ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm bb-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}